
/**
 * Module dependencies.
 */

import { Box, Fill } from 'react-components/layout';
import { Button, Text, Tooltip, UserAvatar, typography } from 'slyk-web-libs/design-system';
import { Icon } from 'react-components/media';
import { Payspace, payspaceSignupModes } from 'slyk-web-libs/types';
import { color, media } from 'react-components/styles';
import { ifProp, theme } from 'styled-tools';
import { isEmpty, toLower } from 'lodash';
import { useBreakpoint } from 'slyk-web-libs/breakpoint';
import { useHistory } from 'react-router';
import { usePayspace } from 'slyk-web-libs/payspace-context';
import { useTranslate } from 'slyk-web-libs/i18n';
import React, { useCallback } from 'react';
import config from 'config';
import payspaceRoutes from 'slyk-web-libs/config/payspace-routes';
import privateIcon from 'client/assets/svg/24/private.svg';
import styled, { css } from 'styled-components';

/**
 * App domain.
 */

const appDomain = config.get<string>('app.domain');

/**
 * Badges types.
 */

const badgesTypes = [
  'hypergrowth',
  'launch',
  'slykpay',
  'slykraise',
  'traction'
];

/*
 * `Props` type.
 */

type Props = {
  downloadAppDisabled: boolean;
  isFixed: boolean;
  onGetApp: () => void;
  showBadges?: boolean;
};

/**
 * Text style.
 */

const textStyle = css`
  max-width: 20ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${media.min('sm')`
    max-width: 25ch;
  `}
`;

/**
 * `Nav` styled component.
 */

const Nav = styled.nav<{ isFixed: boolean }>`
  align-items: center;
  display: grid;
  grid-template-columns: 1fr max-content;
  left: 0;
  padding: 16px 16px 0;
  position: absolute;
  right: 0;
  transition: color ${theme('animations.defaultTransition')};
  z-index: ${theme('zIndex.navbar')};

  ${ifProp('isFixed', css`
    animation: ${theme('keyframes.fadeInDown')} 0.5s ease both;
    padding-bottom: 16px;
    position: fixed;
  `)}

  ${media.min('ms')`
    padding: 32px 24px 0;

    ${ifProp('isFixed', css`
      padding-bottom: 32px;
    `)}
  `}

  ${media.min('lg')`
    padding: 32px 32px 0;

    ${ifProp('isFixed', css`
      padding-bottom: 32px;
    `)}
  `}
`;

/**
 * `Underlay` styled component.
 */

const Underlay = styled(Fill)<{ isFixed: boolean }>`
  background-color: ${color('white')};
  opacity: 0.9;
  transform: translateY(-100%);
  transition: transform ${theme('animations.fastTransition')};
  z-index: -1;

  ${ifProp('isFixed', css`
    transform: translateY(0);
  `)}
`;

/**
 * `PayspaceWrapper` styled component.
 */

const PayspaceWrapper = styled.div`
  display: grid;
  grid-template-areas:
    'info'
    'tags';
  grid-template-columns: 1fr;

  ${media.min('sm')`
    grid-template-areas: 'info tags download';
    grid-template-columns: max-content 1fr max-content;
  `}

  ${media.min('ms')`
    margin: 0 auto;
    max-width: 1632px;
    width: 100%;
  `}
`;

/**
 * `InfoButton` styled component.
 */

const InfoButton = styled.button.attrs({ type: 'button' })`
  align-items: center;
  appearance: none;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  grid-area: info;
  margin: 0;
  outline: none;
  padding: 0;
  position: relative;
  text-decoration: none;

  ${media.min('ms')`
    align-items: start;
  `}
`;

/**
 * `StyledUserAvatar` styled component.
 */

const StyledUserAvatar = styled(UserAvatar)`
  ${typography.styles.heading4}
`;

/**
 * `Domain` styled component.
 */

const Domain = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  margin-left: 16px;
  padding-top: 0;

  ${media.min('ms')`
    margin-left: 24px;
    padding-top: 4px;
  `}
`;

/**
 * `Badges` styled component.
 */

const Badges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  grid-area: tags;
  height: max-content;
  padding-top: 16px;

  ${media.min('sm')`
    margin-left: 16px;
    margin-right: 8px;
    padding-top: 8px;
  `}
`;

/**
 * `Badge` styled component.
 */

const Badge = styled.div<{ isGreen: boolean }>`
  ${typography.styles.textSmall}

  background-color: ${color('white')};
  border: 1px solid ${ifProp('isGreen', '#D3F1CE', '#D1E5FF')};
  border-radius: 8px;
  color: ${ifProp('isGreen', color('success500'), color('primary'))};
  height: max-content;
  padding: 3px 5px;
`;

/**
 * `BadgeIcon` styled component.
 */

const BadgeIcon = styled.span`
  margin-right: 8px;
`;

/**
 * `Slug` styled component.
 */

const Slug = styled(Text)`
  ${textStyle}
  
  color: ${color('secondary')};
  font-weight: 700;
  text-decoration: none;
`;

/**
 * `TooltipContent` styled component.
 */

const TooltipContent = styled(Text).attrs({
  as: 'div',
  size: 'small'
})`
  text-align: center;
`;

/**
 * `PayspaceUrl` styled component.
 */

const PayspaceUrl = styled(Text).attrs({ as: 'div', size: 'small' })`
  ${textStyle}

  color: ${color('gray700')};
`;

/**
 * `LabelForPrivate` styled component.
 */

const LabelForPrivate = styled.div`
  align-items: center;
  background-color: ${color('gray300')};
  border-radius: 8px;
  display: flex;
  padding: 3px 5px;
  width: max-content;
`;

/**
 * `Private` styled component.
 */

const Private = styled(Text).attrs({ size: 'small' })`
  color: ${color('gray7500')};
  margin-left: 4px;
`;

/**
 * Export `Navbar` component.
 */

export function Navbar(props: Props): JSX.Element {
  const { downloadAppDisabled, isFixed, onGetApp, showBadges = true } = props;
  const { translate } = useTranslate();
  const isMobile = useBreakpoint('sm', 'max');
  const isDesktop = useBreakpoint('ms', 'min');
  const { badges, logo, name, signupMode, slug } = usePayspace() as Payspace;
  const isPrivate = signupMode === payspaceSignupModes.private;
  const history = useHistory();
  const handleClick = useCallback(() => history.push(payspaceRoutes.home), [history]);

  return (
    <Nav isFixed={isFixed}>
      <Underlay isFixed={isFixed} />

      <PayspaceWrapper>
        <InfoButton onClick={handleClick}>
          <StyledUserAvatar
            imageUrl={logo?.url}
            name={name}
            size={isDesktop ? '70px' : '48px'}
          />

          <Domain>
            <Slug>
              {name}
            </Slug>

            <PayspaceUrl>
              {`${slug}.${appDomain}`}
            </PayspaceUrl>
          </Domain>
        </InfoButton>

        {(isPrivate || !isEmpty(badges)) && showBadges && (
          <Badges>
            {isPrivate && (
              <LabelForPrivate>
                <Icon
                  aria-hidden
                  color={color('gray500')}
                  icon={privateIcon}
                  size={'20px'}
                />

                {!isMobile && (
                  <Private>
                    {translate('navbar.privateLabel')}
                  </Private>
                )}
              </LabelForPrivate>
            )}

            {!!badges && badges.map((item: string, index: number) => {
              const badge = toLower(item);

              if (badgesTypes.includes(badge)) {
                return (
                  <Tooltip
                    content={
                      <TooltipContent>
                        {translate([
                          `navbar.badges.${badge}.description`,
                          'common.labels.notAvailable'
                        ])}
                      </TooltipContent>
                    }
                    key={index}
                    minWidth={'140px'}
                    placement={'bottom'}
                  >
                    <Badge isGreen={item === 'launch'}>
                      <BadgeIcon>
                        {translate([
                          `navbar.badges.${badge}.icon`,
                          ''
                        ])}
                      </BadgeIcon>

                      {translate([
                        `navbar.badges.${badge}.title`,
                        'common.labels.notAvailable'
                      ])}
                    </Badge>
                  </Tooltip>
                );
              }
            })}
          </Badges>
        )}

        {!isMobile && (
          <Box gridArea={'download'}>
            <Button
              disabled={downloadAppDisabled}
              onClick={onGetApp}
              size={'large'}
            >
              {translate('navbar.getApp')}
            </Button>
          </Box>
        )}
      </PayspaceWrapper>
    </Nav>
  );
}
